import React, { useEffect, useMemo, useState } from "react";
import {
  useWatch,
  useFormState,
  type Path,
  type RegisterOptions,
} from "react-hook-form";
import {
  CASHBACK_EARNED_RULES,
  CASHBACK_LOADING_MESSAGE,
  TONE_BY_TYPE,
} from "../constants";
import { CreateTransactionsObject, TransactionTypeOptions } from "../types";
import {
  AmountCard,
  FormRow,
  SelectSheet,
  SelectSheetOption,
} from "../../../../../components/MobileForm";
import {
  Card,
  CardLabel,
  RowsCard,
  ToggleRow,
  RowLabel,
  HelperText,
} from "../../../../../components/MobileForm/styles";
import {
  FormInput,
  FormMoneyInput,
  InputDate,
} from "../../../../../components/Input";
import { NetPreview } from "../../../../../components/NetPreview";
import { NetToGrossHelper } from "../../../../../components/NetToGrossHelper";
import { amountLabelOf } from "../../../../../utils/taxWithheld";
import { DuplicateBanner } from "../DuplicateBanner";
import { TypeSegments } from "../TypeSegments";
import { ActiveSheet, MobileTransactionFormProps } from "./types";

const toOptions = (
  items: { label: string; value: string | number }[],
): SelectSheetOption[] =>
  items.map((item) => ({ label: item.label, value: String(item.value) }));

export const MobileTransactionForm: React.FC<MobileTransactionFormProps> = ({
  action,
  originalDate,
  control,
  register,
  unregister,
  setValue,
  clearErrors,
  validationRules,
  isSubmitting,
  typeOptions,
  categoryOptions,
  subCategoryOptions,
  accountOptions,
  loanToAccountOptions,
  frequencyOptions,
  shouldShowPrincipalAmount,
  shouldShowTax,
  shouldShowTaxSwitch,
  isTaxSwitchDisabled,
  taxSwitchHelperText,
  shouldShowCategory,
  shouldShowSubCategory,
  shouldShowFromAccount,
  shouldShowToAccount,
  isRecurringDisabled,
  recurringHelperText,
  isRecurring,
  maxTransactionDate,
  cashbackEarnedLabel,
  cashbackEarnedHelper,
  cashbackPrefillError,
  isCashbackPrefillPending,
}) => {
  const [activeSheet, setActiveSheet] = useState<ActiveSheet>(null);
  const closeSheet = () => setActiveSheet(null);

  const values = useWatch({ control });
  const { errors } = useFormState({ control });

  const typeValue = values.transactionType?.value;
  const isLoan = typeValue === TransactionTypeOptions.LOAN_PAYMENT;
  const amountVariant = typeValue ? TONE_BY_TYPE[typeValue] : "neutral";

  const setFieldAndClose =
    (field: Parameters<typeof setValue>[0]) => (value: SelectSheetOption) => {
      setValue(field, value as never, {
        shouldDirty: true,
        shouldValidate: true,
      });
      clearErrors(field);
      closeSheet();
    };

  const handleTypeChange = (option: SelectSheetOption) => {
    setValue("transactionType", option, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // Dropdown fields (selects) are set via bottom sheets, not Controller inputs,
  // so they are registered here purely to attach validation rules; RHF's
  // handleSubmit then validates them on submit. The text/number/date fields are
  // bound through the shared Controller-based inputs, which register themselves.
  const rules = validationRules as Record<
    string,
    RegisterOptions<CreateTransactionsObject>
  >;
  const registerRules = <TName extends Path<CreateTransactionsObject>>(
    name: TName,
    options: RegisterOptions<CreateTransactionsObject>,
  ) =>
    register(name, options as RegisterOptions<CreateTransactionsObject, TName>);

  registerRules("transactionType", rules.transactionType);
  registerRules(
    "fromAccount",
    shouldShowFromAccount ? rules.fromAccountId : {},
  );
  registerRules("toAccount", shouldShowToAccount ? rules.toAccountId : {});
  registerRules("frequency", isRecurring ? rules.frequency : {});

  useEffect(() => {
    if (shouldShowFromAccount) {
      return;
    }

    unregister("fromAccount");
    clearErrors("fromAccount");
  }, [clearErrors, shouldShowFromAccount, unregister]);

  useEffect(() => {
    if (shouldShowToAccount) {
      return;
    }

    unregister("toAccount");
    clearErrors("toAccount");
  }, [clearErrors, shouldShowToAccount, unregister]);

  useEffect(() => {
    if (isRecurring) {
      return;
    }

    unregister("frequency");
    clearErrors("frequency");
  }, [clearErrors, isRecurring, unregister]);

  const categorySelectOptions = useMemo(
    () => toOptions(categoryOptions),
    [categoryOptions],
  );
  const subCategorySelectOptions = useMemo(
    () => toOptions(subCategoryOptions),
    [subCategoryOptions],
  );
  const accountSelectOptions = useMemo(
    () => toOptions(accountOptions),
    [accountOptions],
  );
  const frequencySelectOptions = useMemo(
    () => toOptions(frequencyOptions),
    [frequencyOptions],
  );
  const toAccountSheetOptions = useMemo(
    () => toOptions(isLoan ? loanToAccountOptions : accountOptions),
    [isLoan, loanToAccountOptions, accountOptions],
  );

  const hasSelectRows =
    shouldShowCategory ||
    shouldShowSubCategory ||
    shouldShowFromAccount ||
    shouldShowToAccount;

  return (
    <>
      {action === "duplicate" ? (
        <DuplicateBanner originalDate={originalDate} />
      ) : null}

      <AmountCard
        id="mobile-transaction-amount"
        label={amountLabelOf(shouldShowTax, { required: true })}
        control={control}
        rules={rules.amount}
        tone={amountVariant}
        disabled={isSubmitting}
      >
        <TypeSegments
          options={typeOptions}
          selectedValue={typeValue}
          disabled={isSubmitting}
          onChange={handleTypeChange}
        />
      </AmountCard>

      {cashbackEarnedLabel && (
        <Card>
          <CardLabel htmlFor="mobile-cashback-earned">
            {cashbackEarnedLabel}
          </CardLabel>
          <FormMoneyInput
            id="mobile-cashback-earned"
            name="cashbackEarned"
            control={control}
            disabled={isSubmitting}
            rules={CASHBACK_EARNED_RULES}
          />
          <HelperText>{cashbackEarnedHelper}</HelperText>
        </Card>
      )}
      {isCashbackPrefillPending && (
        <HelperText role="status">{CASHBACK_LOADING_MESSAGE}</HelperText>
      )}
      {cashbackPrefillError && (
        <HelperText role="status">{cashbackPrefillError}</HelperText>
      )}
      {shouldShowTaxSwitch ? (
        <RowsCard>
          <ToggleRow>
            <RowLabel>Tax withheld</RowLabel>
            <input
              type="checkbox"
              data-testid="tax-withheld-toggle"
              {...register("hasTaxWithheld")}
              disabled={isSubmitting || isTaxSwitchDisabled}
            />
          </ToggleRow>
          {taxSwitchHelperText ? (
            <HelperText>{taxSwitchHelperText}</HelperText>
          ) : null}
        </RowsCard>
      ) : null}

      {shouldShowTax ? (
        <Card>
          <CardLabel htmlFor="mobile-transaction-tax">Tax withheld *</CardLabel>
          <FormMoneyInput
            id="mobile-transaction-tax"
            name="taxWithheld"
            control={control}
            rules={rules.taxWithheld}
            placeholder="Enter Tax Withheld"
            disabled={isSubmitting}
          />
          <NetPreview control={control} />
          <NetToGrossHelper setValue={setValue} disabled={isSubmitting} />
        </Card>
      ) : null}

      {shouldShowPrincipalAmount ? (
        <Card>
          <CardLabel htmlFor="mobile-transaction-principal">
            Principal amount *
          </CardLabel>
          <FormMoneyInput
            id="mobile-transaction-principal"
            name="principalAmount"
            control={control}
            rules={rules.principalAmount}
            placeholder="Enter Principal Amount"
            disabled={isSubmitting}
          />
        </Card>
      ) : null}

      <Card>
        <CardLabel htmlFor="mobile-transaction-date">Date *</CardLabel>
        <InputDate
          id="mobile-transaction-date"
          name="transactionDate"
          control={control}
          rules={rules.transactionDate}
          placeholder="YYYY/MM/DD"
          maxDate={maxTransactionDate}
          disabled={isSubmitting}
        />
      </Card>

      {hasSelectRows ? (
        <RowsCard>
          {shouldShowCategory ? (
            <FormRow
              label="Category"
              value={values.category?.label}
              placeholder="Select category"
              onClick={() => setActiveSheet("category")}
            />
          ) : null}
          {shouldShowSubCategory ? (
            <FormRow
              label="Subcategory"
              value={values.subCategory?.label}
              placeholder="Select subcategory"
              onClick={() => setActiveSheet("subCategory")}
            />
          ) : null}
          {shouldShowFromAccount ? (
            <FormRow
              label="From account *"
              value={values.fromAccount?.label}
              placeholder="Select account"
              error={errors.fromAccount?.message}
              onClick={() => setActiveSheet("fromAccount")}
            />
          ) : null}
          {shouldShowToAccount ? (
            <FormRow
              label="To account *"
              value={values.toAccount?.label}
              placeholder="Select account"
              error={errors.toAccount?.message}
              onClick={() => setActiveSheet("toAccount")}
            />
          ) : null}
        </RowsCard>
      ) : null}

      <Card>
        <CardLabel htmlFor="mobile-transaction-note">Note</CardLabel>
        <FormInput
          id="mobile-transaction-note"
          name="note"
          control={control}
          rules={rules.note}
          placeholder="Add a note"
          handleClear={false}
          disabled={isSubmitting}
        />
      </Card>

      <RowsCard>
        <ToggleRow>
          <RowLabel>Recurring transaction</RowLabel>
          <input
            type="checkbox"
            data-testid="recurring-toggle"
            {...register("isRecurring")}
            disabled={isSubmitting || isRecurringDisabled}
          />
        </ToggleRow>
        {recurringHelperText ? (
          <HelperText>{recurringHelperText}</HelperText>
        ) : null}
        {isRecurring ? (
          <FormRow
            label="Frequency *"
            value={values.frequency?.label}
            placeholder="Select frequency"
            error={errors.frequency?.message}
            onClick={() => setActiveSheet("frequency")}
          />
        ) : null}
      </RowsCard>

      {isRecurring ? (
        <Card>
          <CardLabel htmlFor="mobile-transaction-interval">
            Interval *
          </CardLabel>
          <FormInput
            id="mobile-transaction-interval"
            name="interval"
            control={control}
            rules={rules.interval}
            type="number"
            placeholder="Enter interval"
            handleClear={false}
            disabled={isSubmitting}
          />

          <CardLabel htmlFor="mobile-transaction-stop-after">
            Stop after occurrences
          </CardLabel>
          <FormInput
            id="mobile-transaction-stop-after"
            name="stopAfterOccurrences"
            control={control}
            rules={rules.stopAfterOccurrences}
            type="number"
            placeholder="Occurrences or empty for no limit"
            handleClear={false}
            disabled={isSubmitting}
          />
        </Card>
      ) : null}

      <SelectSheet
        isOpen={activeSheet === "category"}
        title="Select category"
        options={categorySelectOptions}
        selectedValue={values.category?.value}
        onSelect={setFieldAndClose("category")}
        onClose={closeSheet}
      />
      <SelectSheet
        isOpen={activeSheet === "subCategory"}
        title="Select subcategory"
        options={subCategorySelectOptions}
        selectedValue={values.subCategory?.value}
        onSelect={setFieldAndClose("subCategory")}
        onClose={closeSheet}
      />
      <SelectSheet
        isOpen={activeSheet === "fromAccount"}
        title="Select account"
        options={accountSelectOptions}
        selectedValue={values.fromAccount?.value}
        onSelect={setFieldAndClose("fromAccount")}
        onClose={closeSheet}
      />
      <SelectSheet
        isOpen={activeSheet === "toAccount"}
        title="Select account"
        options={toAccountSheetOptions}
        selectedValue={values.toAccount?.value}
        onSelect={setFieldAndClose("toAccount")}
        onClose={closeSheet}
      />
      <SelectSheet
        isOpen={activeSheet === "frequency"}
        title="Select frequency"
        options={frequencySelectOptions}
        selectedValue={values.frequency?.value}
        onSelect={setFieldAndClose("frequency")}
        onClose={closeSheet}
      />
    </>
  );
};
